import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase, type SxProps, type Theme } from "@mui/material";
import { useEffect, useState } from "react";

import { mergeSxProps } from "../../lib/mergeSxProps";
import { CbhIcon } from "../CbhIcon";
import { Text } from "../Text";

export interface FilePreviewTileProps {
  fileUrl: string;
  alt?: string;
  onPress?: () => void;
  onRemove?: () => void;
  sx?: SxProps<Theme>;
  /** File extension (e.g., "pdf", "png"). When provided, enables fallback UI on image load failure. */
  fileExtension?: string;
  /** Display name for the file, shown in fallback UI. If omitted, the uppercased fileExtension is shown instead. */
  fileName?: string;
}

function splitFileName(fileName: string): { name: string; extension?: string } {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex > 0) {
    return { name: fileName.slice(0, lastDotIndex), extension: fileName.slice(lastDotIndex) };
  }

  return { name: fileName };
}

function FallbackFileName(props: { fileName: string }) {
  const { name, extension } = splitFileName(props.fileName);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "baseline",
        maxWidth: "90%",
        color: theme.palette.text.secondary,
      })}
    >
      <Text
        semibold
        inline
        variant="caption"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Text>
      {isDefined(extension) && (
        <Text semibold inline variant="caption" sx={{ flexShrink: 0 }}>
          {extension}
        </Text>
      )}
    </Box>
  );
}

export function FilePreviewTile(props: FilePreviewTileProps) {
  const { alt, fileUrl, fileExtension, fileName, onPress, onRemove, sx } = props;
  const [hasImageError, setHasImageError] = useState(false);

  // Reset error state when fileUrl changes so the new image gets a chance to load.
  // Current callers render with key={file.id} so React remounts per file, but this
  // guards against reuse of the same instance with a different URL.
  useEffect(() => {
    setHasImageError(false);
  }, [fileUrl]);

  const shouldShowFallback = isDefined(fileExtension) && hasImageError;

  const content = shouldShowFallback ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        padding: 4,
        gap: 2,
      }}
    >
      <CbhIcon type="document" size="large" />
      {isDefined(fileName) ? (
        <FallbackFileName fileName={fileName} />
      ) : (
        <Text
          semibold
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textAlign: "center",
          })}
        >
          {fileExtension.toUpperCase()}
        </Text>
      )}
    </Box>
  ) : (
    <Box
      component="img"
      src={fileUrl}
      alt={alt}
      style={{
        width: "100%",
        height: "100%",
        aspectRatio: "1/1",
        objectFit: "contain",
      }}
      onError={() => {
        setHasImageError(true);
      }}
    />
  );

  return (
    <Box
      sx={mergeSxProps(
        (theme) => ({
          position: "relative",
          borderRadius: theme.borderRadius?.small,
          border: `1px solid ${theme.palette.border?.primary}`,
          overflow: "hidden",
          aspectRatio: "1/1",
        }),
        sx
      )}
    >
      {onPress ? (
        <ButtonBase sx={{ display: "block", width: "100%", height: "100%" }} onClick={onPress}>
          {content}
        </ButtonBase>
      ) : (
        content
      )}

      {onRemove && (
        <ButtonBase
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(3),
            right: theme.spacing(3),
            backgroundColor: theme.palette.background.invert,
            borderRadius: "50%",
            display: "flex",
            padding: 2,
            color: theme.palette.text?.invertPrimary,
          })}
          onClick={onRemove}
        >
          <CbhIcon type="close" size="small" />
        </ButtonBase>
      )}
    </Box>
  );
}
